import {FACTIONS} from '../constants/factions';
import {UNIT_TYPES} from './units';

/**
 * Свои звуки отрядов фракций — под их особый облик: волки и кабаны орков
 * вместо лошадей, хохот гоблинов вместо человеческого клича, магическое свечение
 * под кличем просветленных.
 *
 * FACTION_UNIT_SOUNDS[factionId][unitTypeId] = {select, move}:
 * - select — выбор и найм отряда (вместо select_unit_*);
 * - move — ход отряда (вместо move_*).
 * Значение — имя звука из FACTION_UNIT_SOUND_FILES или массив имен (звучит
 * случайный). Чего нет — звучит общий звук отряда.
 *
 * Звучат только у отрядов, нарисованных обликом фракции (то же правило, что у
 * картинки на поле, — checkIfUnitHasFactionSkin; исключение —
 * FACTIONS_WITH_UNIT_SOUNDS_WITHOUT_SKIN). Катапульты (d4) звучат
 * одинаково у всех фракций.
 *
 * Файлы — отложенные звуки (configs/deferredSounds): качаются в фоне после
 * загрузки матча и только для фракций, чьи отряды в партии нарисованы своим
 * обликом. Пока файл не загружен, звучит общий звук.
 */

const D6 = UNIT_TYPES.D6.id;
const D10 = UNIT_TYPES.D10.id;
const D20 = UNIT_TYPES.D20.id;
const D8 = UNIT_TYPES.D8.id;
const D12 = UNIT_TYPES.D12.id;

/**
 * Файлы своих звуков отрядов: имя звука → файл и громкость (как у отложенных
 * звуков). Записи с Uppbeat нарезаны и выровнены по громкости под общие звуки
 * того же события (исходники, авторы и сборка — developer_artifacts/
 * art_sources/unit_sounds), поэтому множитель у всех 1
 */
export const FACTION_UNIT_SOUND_FILES = Object.freeze({
  unit_orcs_d6: Object.freeze({path: '/sounds/units/orcs_d6.mp3', volume: 1}),
  unit_orcs_d10: Object.freeze({path: '/sounds/units/orcs_d10.mp3', volume: 1}),
  unit_orcs_d20: Object.freeze({path: '/sounds/units/orcs_d20.mp3', volume: 1}),
  unit_goblins_d6: Object.freeze({path: '/sounds/units/goblins_d6.mp3', volume: 1}),
  unit_goblins_d10: Object.freeze({path: '/sounds/units/goblins_d10.mp3', volume: 1}),
  unit_goblins_d20: Object.freeze({path: '/sounds/units/goblins_d20.mp3', volume: 1}),
  // Волки орков и гоблинов: вой при выборе, бег лап вместо копыт
  unit_wolf_howl: Object.freeze({path: '/sounds/units/wolf_howl.mp3', volume: 1}),
  unit_wolf_run: Object.freeze({path: '/sounds/units/wolf_run.mp3', volume: 1}),
  // Кабаны орков, гоблинов и зверолюдов: визг; ходят под общий стук копыт
  unit_boar_1: Object.freeze({path: '/sounds/units/boar_1.mp3', volume: 1}),
  unit_boar_grunt: Object.freeze({path: '/sounds/units/boar_grunt.mp3', volume: 1}),
  unit_boar_move: Object.freeze({path: '/sounds/units/boar_move.mp3', volume: 1}),
  unit_boar_move_light: Object.freeze({path: '/sounds/units/boar_move_light.mp3', volume: 1}),
  unit_leprechauns_d6: Object.freeze({path: '/sounds/units/leprechauns_d6.mp3', volume: 1}),
  unit_leprechauns_d10: Object.freeze({path: '/sounds/units/leprechauns_d10.mp3', volume: 1}),
  unit_leprechauns_d20: Object.freeze({path: '/sounds/units/leprechauns_d20.mp3', volume: 1}),
  unit_demons_d6: Object.freeze({path: '/sounds/units/demons_d6.mp3', volume: 1}),
  unit_demons_d10: Object.freeze({path: '/sounds/units/demons_d10.mp3', volume: 1}),
  unit_demons_d20: Object.freeze({path: '/sounds/units/demons_d20.mp3', volume: 1}),
  // Рогатые кони демонов и суккубов: злое ржание, у тяжелого — ниже, с рыком
  unit_demon_steed_d8: Object.freeze({path: '/sounds/units/demon_steed_d8.mp3', volume: 1}),
  unit_demon_steed_d12: Object.freeze({path: '/sounds/units/demon_steed_d12.mp3', volume: 1}),
  unit_succubes_d6: Object.freeze({path: '/sounds/units/succubes_d6.mp3', volume: 1}),
  unit_succubes_d10: Object.freeze({path: '/sounds/units/succubes_d10.mp3', volume: 1}),
  unit_succubes_d20: Object.freeze({path: '/sounds/units/succubes_d20.mp3', volume: 1}),
  // Ход пехоты суккубов — толпа на каблуках, у обычной выше, легче и мягче
  unit_succubes_move_d6: Object.freeze({path: '/sounds/units/succubes_move_d6.mp3', volume: 1}),
  unit_succubes_move_d10: Object.freeze({path: '/sounds/units/succubes_move_d10.mp3', volume: 1}),
  unit_succubes_move: Object.freeze({path: '/sounds/units/succubes_move.mp3', volume: 1}),
  unit_beastmen_d6: Object.freeze({path: '/sounds/units/beastmen_d6.mp3', volume: 1}),
  unit_beastmen_d10: Object.freeze({path: '/sounds/units/beastmen_d10.mp3', volume: 1}),
  unit_beastmen_d20: Object.freeze({path: '/sounds/units/beastmen_d20.mp3', volume: 1}),
  // Ход пехоты зверолюдов — глухие копыта по земле, у отрядов мельче выше, легче и мягче
  unit_beastmen_move_d6: Object.freeze({path: '/sounds/units/beastmen_move_d6.mp3', volume: 1}),
  unit_beastmen_move_d10: Object.freeze({path: '/sounds/units/beastmen_move_d10.mp3', volume: 1}),
  unit_beastmen_move: Object.freeze({path: '/sounds/units/beastmen_move.mp3', volume: 1}),
  // Элементали — живое пламя: вспышки огня вместо клича, на ходу — общий топот
  // с гулом огня под ним; огненные кони — ржание общего звука с пламенем под ним
  unit_elementals_d6: Object.freeze({path: '/sounds/units/elementals_d6.mp3', volume: 1}),
  unit_elementals_d10: Object.freeze({path: '/sounds/units/elementals_d10.mp3', volume: 1}),
  unit_elementals_d20: Object.freeze({path: '/sounds/units/elementals_d20.mp3', volume: 1}),
  unit_elementals_move_d6: Object.freeze({path: '/sounds/units/elementals_move_d6.mp3', volume: 1}),
  unit_elementals_move_d10: Object.freeze({path: '/sounds/units/elementals_move_d10.mp3', volume: 1}),
  unit_elementals_move_d20: Object.freeze({path: '/sounds/units/elementals_move_d20.mp3', volume: 1}),
  unit_fire_steed_d8: Object.freeze({path: '/sounds/units/fire_steed_d8.mp3', volume: 1}),
  unit_fire_steed_d12: Object.freeze({path: '/sounds/units/fire_steed_d12.mp3', volume: 1}),
  unit_fire_steed_move: Object.freeze({path: '/sounds/units/fire_steed_move.mp3', volume: 1}),
  // Просветленные: общий клич и ржание с магическим свечением — у каждого отряда своим
  unit_enlightened_d6: Object.freeze({path: '/sounds/units/enlightened_d6.mp3', volume: 1}),
  unit_enlightened_d10: Object.freeze({path: '/sounds/units/enlightened_d10.mp3', volume: 1}),
  unit_enlightened_d20: Object.freeze({path: '/sounds/units/enlightened_d20.mp3', volume: 1}),
  unit_enlightened_d8: Object.freeze({path: '/sounds/units/enlightened_d8.mp3', volume: 1}),
  unit_enlightened_d12: Object.freeze({path: '/sounds/units/enlightened_d12.mp3', volume: 1}),
  // Селестиалы: общий клич с ангельским хором, пехота на ходу перелетает на ангельских крыльях
  // (у отрядов мельче выше, легче и мягче); пегас — ржание с крыльями и взмахи
  unit_celectials_move_d6: Object.freeze({path: '/sounds/units/celectials_move_d6.mp3', volume: 1}),
  unit_celectials_move_d10: Object.freeze({path: '/sounds/units/celectials_move_d10.mp3', volume: 1}),
  unit_celectials_move: Object.freeze({path: '/sounds/units/celectials_move.mp3', volume: 1}),
  // на ходу; тяжелый конь — ржание со светлой магией
  unit_celectials_d6: Object.freeze({path: '/sounds/units/celectials_d6.mp3', volume: 1}),
  unit_celectials_d10: Object.freeze({path: '/sounds/units/celectials_d10.mp3', volume: 1}),
  unit_celectials_d20: Object.freeze({path: '/sounds/units/celectials_d20.mp3', volume: 1}),
  unit_pegasus: Object.freeze({path: '/sounds/units/pegasus.mp3', volume: 1}),
  unit_pegasus_move: Object.freeze({path: '/sounds/units/pegasus_move.mp3', volume: 1}),
  unit_holy_steed: Object.freeze({path: '/sounds/units/holy_steed.mp3', volume: 1}),
  unit_holy_steed_move: Object.freeze({path: '/sounds/units/holy_steed_move.mp3', volume: 1}),
  // Эльфы: общий клич и ржание с колокольчиками или арфой и трелью птиц;
  // единорог — с мерцанием
  unit_elves_d6: Object.freeze({path: '/sounds/units/elves_d6.mp3', volume: 1}),
  unit_elves_d10: Object.freeze({path: '/sounds/units/elves_d10.mp3', volume: 1}),
  unit_elves_d20: Object.freeze({path: '/sounds/units/elves_d20.mp3', volume: 1}),
  unit_elves_d8: Object.freeze({path: '/sounds/units/elves_d8.mp3', volume: 1}),
  unit_unicorn: Object.freeze({path: '/sounds/units/unicorn.mp3', volume: 1}),
  // Люди: общий клич и ржание с сопровождением римского войска — мечи, барабан, рог, труба
  unit_humans_d6: Object.freeze({path: '/sounds/units/humans_d6.mp3', volume: 1}),
  unit_humans_d10: Object.freeze({path: '/sounds/units/humans_d10.mp3', volume: 1}),
  unit_humans_d20: Object.freeze({path: '/sounds/units/humans_d20.mp3', volume: 1}),
  unit_humans_d8: Object.freeze({path: '/sounds/units/humans_d8.mp3', volume: 1}),
  unit_humans_d12: Object.freeze({path: '/sounds/units/humans_d12.mp3', volume: 1}),
  // Вентори: общий клич и ржание со звоном монет или кассой под искрой магии
  unit_ventory_d6: Object.freeze({path: '/sounds/units/ventory_d6.mp3', volume: 1}),
  unit_ventory_d10: Object.freeze({path: '/sounds/units/ventory_d10.mp3', volume: 1}),
  unit_ventory_d20: Object.freeze({path: '/sounds/units/ventory_d20.mp3', volume: 1}),
  unit_ventory_d8: Object.freeze({path: '/sounds/units/ventory_d8.mp3', volume: 1}),
  unit_ventory_d12: Object.freeze({path: '/sounds/units/ventory_d12.mp3', volume: 1}),
  // Гномы: короткий боевой выкрик ниже обычного
  unit_dwarves_d6: Object.freeze({path: '/sounds/units/dwarves_d6.mp3', volume: 1}),
  unit_dwarves_d10: Object.freeze({path: '/sounds/units/dwarves_d10.mp3', volume: 1}),
  unit_dwarves_d20: Object.freeze({path: '/sounds/units/dwarves_d20.mp3', volume: 1}),
  // Аквары — водяные ящеры: рык рептилии с мокрым кваканьем, змееящеры шипят; на ходу шаги по воде,
  // у отрядов мельче шаги выше, легче и мягче
  unit_aquars_d6: Object.freeze({path: '/sounds/units/aquars_d6.mp3', volume: 1}),
  unit_aquars_d10: Object.freeze({path: '/sounds/units/aquars_d10.mp3', volume: 1}),
  unit_aquars_d20: Object.freeze({path: '/sounds/units/aquars_d20.mp3', volume: 1}),
  unit_aquars_d8: Object.freeze({path: '/sounds/units/aquars_d8.mp3', volume: 1}),
  unit_aquars_d12: Object.freeze({path: '/sounds/units/aquars_d12.mp3', volume: 1}),
  unit_aquars_move_d6: Object.freeze({path: '/sounds/units/aquars_move_d6.mp3', volume: 1}),
  unit_aquars_move_d10: Object.freeze({path: '/sounds/units/aquars_move_d10.mp3', volume: 1}),
  unit_aquars_move: Object.freeze({path: '/sounds/units/aquars_move.mp3', volume: 1}),
  unit_aquars_mount_move_d8: Object.freeze({path: '/sounds/units/aquars_mount_move_d8.mp3', volume: 1}),
  unit_aquars_mount_move: Object.freeze({path: '/sounds/units/aquars_mount_move.mp3', volume: 1}),
  // Темные эльфы: общий клич с шепотом и темной магией, ящеры шипят и бегут на когтях
  unit_dark_elves_d6: Object.freeze({path: '/sounds/units/dark_elves_d6.mp3', volume: 1}),
  unit_dark_elves_d10: Object.freeze({path: '/sounds/units/dark_elves_d10.mp3', volume: 1}),
  unit_dark_elves_d20: Object.freeze({path: '/sounds/units/dark_elves_d20.mp3', volume: 1}),
  unit_dark_elves_d8: Object.freeze({path: '/sounds/units/dark_elves_d8.mp3', volume: 1}),
  unit_dark_elves_d12: Object.freeze({path: '/sounds/units/dark_elves_d12.mp3', volume: 1}),
  unit_dark_elves_mount_move: Object.freeze({path: '/sounds/units/dark_elves_mount_move.mp3', volume: 1}),
  // Допплеры: общий клич и ржание, под ними плоть меняет форму или звучит превращение
  unit_dopplers_d6: Object.freeze({path: '/sounds/units/dopplers_d6.mp3', volume: 1}),
  unit_dopplers_d10: Object.freeze({path: '/sounds/units/dopplers_d10.mp3', volume: 1}),
  unit_dopplers_d20: Object.freeze({path: '/sounds/units/dopplers_d20.mp3', volume: 1}),
  unit_dopplers_d8: Object.freeze({path: '/sounds/units/dopplers_d8.mp3', volume: 1}),
  unit_dopplers_d12: Object.freeze({path: '/sounds/units/dopplers_d12.mp3', volume: 1}),
  // Дракон: рев; на ходу взмах огромного крыла
  unit_dragons_d20: Object.freeze({path: '/sounds/units/dragons_d20.mp3', volume: 1}),
  unit_dragons_move: Object.freeze({path: '/sounds/units/dragons_move.mp3', volume: 1}),
  // Энты: скрип и треск дерева, олень и лев с шелестом травы; пехота на ходу — удары дерева о землю и скрип
  // (у отрядов мельче выше, легче и мягче), олень — шаг по листве, полулев-полуэнт — зверь через траву со скрипом дерева
  unit_ents_d6: Object.freeze({path: '/sounds/units/ents_d6.mp3', volume: 1}),
  unit_ents_d10: Object.freeze({path: '/sounds/units/ents_d10.mp3', volume: 1}),
  unit_ents_d20: Object.freeze({path: '/sounds/units/ents_d20.mp3', volume: 1}),
  unit_ents_d8: Object.freeze({path: '/sounds/units/ents_d8.mp3', volume: 1}),
  unit_ents_d12: Object.freeze({path: '/sounds/units/ents_d12.mp3', volume: 1}),
  unit_ents_move_d6: Object.freeze({path: '/sounds/units/ents_move_d6.mp3', volume: 1}),
  unit_ents_move_d10: Object.freeze({path: '/sounds/units/ents_move_d10.mp3', volume: 1}),
  unit_ents_move: Object.freeze({path: '/sounds/units/ents_move.mp3', volume: 1}),
  unit_ents_mount_move: Object.freeze({path: '/sounds/units/ents_mount_move.mp3', volume: 1}),
  unit_ents_lion_move: Object.freeze({path: '/sounds/units/ents_lion_move.mp3', volume: 1}),
  // Феникс: крик орла с пламенем, на ходу крылья с гулом огня
  unit_fenix_d20: Object.freeze({path: '/sounds/units/fenix_d20.mp3', volume: 1}),
  unit_fenix_move: Object.freeze({path: '/sounds/units/fenix_move.mp3', volume: 1}),
  // Гремлины: пехота — механик (смешок с трещоткой), автоматон — паровой робот, на ходу тяжелые металлические шаги
  unit_gremlins_d6: Object.freeze({path: '/sounds/units/gremlins_d6.mp3', volume: 1}),
  unit_gremlins_d20: Object.freeze({path: '/sounds/units/gremlins_d20.mp3', volume: 1}),
  unit_gremlins_d20_move: Object.freeze({path: '/sounds/units/gremlins_d20_move.mp3', volume: 1}),
  // Грибной народ: хрип мертвеца с хлюпаньем слизи, ящер шипит
  unit_mushroom_people_d6: Object.freeze({path: '/sounds/units/mushroom_people_d6.mp3', volume: 1}),
  unit_mushroom_people_d10: Object.freeze({path: '/sounds/units/mushroom_people_d10.mp3', volume: 1}),
  unit_mushroom_people_d20: Object.freeze({path: '/sounds/units/mushroom_people_d20.mp3', volume: 1}),
  unit_mushroom_people_d8: Object.freeze({path: '/sounds/units/mushroom_people_d8.mp3', volume: 1}),
  unit_mushroom_people_move_d6: Object.freeze({path: '/sounds/units/mushroom_people_move_d6.mp3', volume: 1}),
  unit_mushroom_people_move_d10: Object.freeze({path: '/sounds/units/mushroom_people_move_d10.mp3', volume: 1}),
  unit_mushroom_people_move: Object.freeze({path: '/sounds/units/mushroom_people_move.mp3', volume: 1}),
  // Духи: далекий стон и порыв на ходу
  unit_spirits_d10: Object.freeze({path: '/sounds/units/spirits_d10.mp3', volume: 1}),
  unit_spirits_move: Object.freeze({path: '/sounds/units/spirits_move.mp3', volume: 1}),
  // Техногномы: короткий выкрик с паром и шестернями
  unit_technodwarves_d6: Object.freeze({path: '/sounds/units/technodwarves_d6.mp3', volume: 1}),
  unit_technodwarves_d10: Object.freeze({path: '/sounds/units/technodwarves_d10.mp3', volume: 1}),
  unit_technodwarves_d20: Object.freeze({path: '/sounds/units/technodwarves_d20.mp3', volume: 1}),
  unit_technodwarves_move_d6: Object.freeze({path: '/sounds/units/technodwarves_move_d6.mp3', volume: 1}),
  unit_technodwarves_move_d10: Object.freeze({path: '/sounds/units/technodwarves_move_d10.mp3', volume: 1}),
  unit_technodwarves_move: Object.freeze({path: '/sounds/units/technodwarves_move.mp3', volume: 1}),
  // Нежить: хруст костей и стон, рев мертвеца, вой призрака, вампир шипит под орган
  unit_undead_d6: Object.freeze({path: '/sounds/units/undead_d6.mp3', volume: 1}),
  unit_undead_d10: Object.freeze({path: '/sounds/units/undead_d10.mp3', volume: 1}),
  unit_undead_d20: Object.freeze({path: '/sounds/units/undead_d20.mp3', volume: 1}),
  unit_undead_d8: Object.freeze({path: '/sounds/units/undead_d8.mp3', volume: 1}),
  unit_undead_d12: Object.freeze({path: '/sounds/units/undead_d12.mp3', volume: 1}),
  unit_undead_move_d6: Object.freeze({path: '/sounds/units/undead_move_d6.mp3', volume: 1}),
  unit_undead_move_d10: Object.freeze({path: '/sounds/units/undead_move_d10.mp3', volume: 1}),
  unit_undead_move: Object.freeze({path: '/sounds/units/undead_move.mp3', volume: 1}),
  unit_undead_ghost_move: Object.freeze({path: '/sounds/units/undead_ghost_move.mp3', volume: 1}),
  unit_undead_vampire_move: Object.freeze({path: '/sounds/units/undead_vampire_move.mp3', volume: 1}),
  // Волшебники: маги говорят фразу из озвучки игры, остальные — общий клич и ржание с заклинанием
  unit_wizards_d6: Object.freeze({path: '/sounds/units/wizards_d6.mp3', volume: 1}),
  unit_wizards_d10: Object.freeze({path: '/sounds/units/wizards_d10.mp3', volume: 1}),
  unit_wizards_d20: Object.freeze({path: '/sounds/units/wizards_d20.mp3', volume: 1}),
  unit_wizards_d8: Object.freeze({path: '/sounds/units/wizards_d8.mp3', volume: 1}),
  unit_wizards_d12: Object.freeze({path: '/sounds/units/wizards_d12.mp3', volume: 1}),
  // Инсектоиды: стрекот и визг, на ходу — топот лапок, у отрядов мельче он выше, легче и мягче
  unit_insectoids_d6: Object.freeze({path: '/sounds/units/insectoids_d6.mp3', volume: 1}),
  unit_insectoids_d10: Object.freeze({path: '/sounds/units/insectoids_d10.mp3', volume: 1}),
  unit_insectoids_d20: Object.freeze({path: '/sounds/units/insectoids_d20.mp3', volume: 1}),
  unit_insectoids_d8: Object.freeze({path: '/sounds/units/insectoids_d8.mp3', volume: 1}),
  unit_insectoids_d12: Object.freeze({path: '/sounds/units/insectoids_d12.mp3', volume: 1}),
  unit_insectoids_move_d6: Object.freeze({path: '/sounds/units/insectoids_move_d6.mp3', volume: 1}),
  unit_insectoids_move_d10: Object.freeze({path: '/sounds/units/insectoids_move_d10.mp3', volume: 1}),
  unit_insectoids_move: Object.freeze({path: '/sounds/units/insectoids_move.mp3', volume: 1}),
  unit_insectoids_mount_move_d8: Object.freeze({path: '/sounds/units/insectoids_mount_move_d8.mp3', volume: 1}),
  unit_insectoids_mount_move: Object.freeze({path: '/sounds/units/insectoids_mount_move.mp3', volume: 1}),
  // Крысолюды: рык с крысиным визгом, у обычной пехоты голос мельче;
  // на ходу когти и тяжелая поступь, у отрядов мельче шаги выше, легче и мягче
  unit_ratmen_d6: Object.freeze({path: '/sounds/units/ratmen_d6.mp3', volume: 1}),
  unit_ratmen_d10: Object.freeze({path: '/sounds/units/ratmen_d10.mp3', volume: 1}),
  unit_ratmen_d20: Object.freeze({path: '/sounds/units/ratmen_d20.mp3', volume: 1}),
  unit_ratmen_d8: Object.freeze({path: '/sounds/units/ratmen_d8.mp3', volume: 1}),
  unit_ratmen_d12: Object.freeze({path: '/sounds/units/ratmen_d12.mp3', volume: 1}),
  unit_ratmen_move_d6: Object.freeze({path: '/sounds/units/ratmen_move_d6.mp3', volume: 1}),
  unit_ratmen_move_d10: Object.freeze({path: '/sounds/units/ratmen_move_d10.mp3', volume: 1}),
  unit_ratmen_move: Object.freeze({path: '/sounds/units/ratmen_move.mp3', volume: 1}),
  unit_ratmen_mount_move_d8: Object.freeze({path: '/sounds/units/ratmen_mount_move_d8.mp3', volume: 1}),
  unit_ratmen_mount_move: Object.freeze({path: '/sounds/units/ratmen_mount_move.mp3', volume: 1}),
  // Великан: рев и тяжелые шаги из озвучки выбора фракции
  unit_giants_d20: Object.freeze({path: '/sounds/units/giants_d20.mp3', volume: 1}),
  unit_giants_move: Object.freeze({path: '/sounds/units/giants_move.mp3', volume: 1}),
});

const WOLF = Object.freeze({select: 'unit_wolf_howl', move: 'unit_wolf_run'});
const BOAR = Object.freeze({select: 'unit_boar_1', move: 'unit_boar_move'});
const DEMON_STEED_D8 = Object.freeze({select: 'unit_demon_steed_d8'});
const DEMON_STEED_D12 = Object.freeze({select: 'unit_demon_steed_d12'});

export const FACTION_UNIT_SOUNDS = Object.freeze({
  [FACTIONS.ORCS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_orcs_d6'}),
    [D10]: Object.freeze({select: 'unit_orcs_d10'}),
    [D20]: Object.freeze({select: 'unit_orcs_d20'}),
    [D8]: WOLF,
    [D12]: BOAR,
  }),
  [FACTIONS.GOBLINS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_goblins_d6'}),
    [D10]: Object.freeze({select: 'unit_goblins_d10'}),
    [D20]: Object.freeze({select: 'unit_goblins_d20'}),
    [D8]: WOLF,
    [D12]: BOAR,
  }),
  [FACTIONS.LEPRECHAUNS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_leprechauns_d6'}),
    [D10]: Object.freeze({select: 'unit_leprechauns_d10'}),
    [D20]: Object.freeze({select: 'unit_leprechauns_d20'}),
  }),
  [FACTIONS.DEMONS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_demons_d6'}),
    [D10]: Object.freeze({select: 'unit_demons_d10'}),
    [D20]: Object.freeze({select: 'unit_demons_d20'}),
    [D8]: DEMON_STEED_D8,
    [D12]: DEMON_STEED_D12,
  }),
  [FACTIONS.SUCCUBES]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_succubes_d6', move: 'unit_succubes_move_d6'}),
    [D10]: Object.freeze({select: 'unit_succubes_d10', move: 'unit_succubes_move_d10'}),
    [D20]: Object.freeze({select: 'unit_succubes_d20', move: 'unit_succubes_move'}),
    [D8]: DEMON_STEED_D8,
    [D12]: DEMON_STEED_D12,
  }),
  [FACTIONS.BEASTMEN]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_beastmen_d6', move: 'unit_beastmen_move_d6'}),
    [D10]: Object.freeze({select: 'unit_beastmen_d10', move: 'unit_beastmen_move_d10'}),
    [D20]: Object.freeze({select: 'unit_beastmen_d20', move: 'unit_beastmen_move'}),
    // Легкий кабан хрюкает, тяжелый визжит
    [D8]: Object.freeze({select: 'unit_boar_grunt', move: 'unit_boar_move_light'}),
    [D12]: BOAR,
  }),
  [FACTIONS.ELEMENTALS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_elementals_d6', move: 'unit_elementals_move_d6'}),
    [D10]: Object.freeze({select: 'unit_elementals_d10', move: 'unit_elementals_move_d10'}),
    [D20]: Object.freeze({select: 'unit_elementals_d20', move: 'unit_elementals_move_d20'}),
    [D8]: Object.freeze({select: 'unit_fire_steed_d8', move: 'unit_fire_steed_move'}),
    [D12]: Object.freeze({select: 'unit_fire_steed_d12', move: 'unit_fire_steed_move'}),
  }),
  [FACTIONS.ENLIGHTENED]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_enlightened_d6'}),
    [D10]: Object.freeze({select: 'unit_enlightened_d10'}),
    [D20]: Object.freeze({select: 'unit_enlightened_d20'}),
    [D8]: Object.freeze({select: 'unit_enlightened_d8'}),
    [D12]: Object.freeze({select: 'unit_enlightened_d12'}),
  }),
  [FACTIONS.CELECTIALS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_celectials_d6', move: 'unit_celectials_move_d6'}),
    [D10]: Object.freeze({select: 'unit_celectials_d10', move: 'unit_celectials_move_d10'}),
    [D20]: Object.freeze({select: 'unit_celectials_d20', move: 'unit_celectials_move'}),
    [D8]: Object.freeze({select: 'unit_pegasus', move: 'unit_pegasus_move'}),
    [D12]: Object.freeze({select: 'unit_holy_steed', move: 'unit_holy_steed_move'}),
  }),
  [FACTIONS.ELVES]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_elves_d6'}),
    [D10]: Object.freeze({select: 'unit_elves_d10'}),
    [D20]: Object.freeze({select: 'unit_elves_d20'}),
    [D8]: Object.freeze({select: 'unit_elves_d8'}),
    [D12]: Object.freeze({select: 'unit_unicorn'}),
  }),
  [FACTIONS.HUMANS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_humans_d6'}),
    [D10]: Object.freeze({select: 'unit_humans_d10'}),
    [D20]: Object.freeze({select: 'unit_humans_d20'}),
    [D8]: Object.freeze({select: 'unit_humans_d8'}),
    [D12]: Object.freeze({select: 'unit_humans_d12'}),
  }),
  [FACTIONS.VENTORY]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_ventory_d6'}),
    [D10]: Object.freeze({select: 'unit_ventory_d10'}),
    [D20]: Object.freeze({select: 'unit_ventory_d20'}),
    [D8]: Object.freeze({select: 'unit_ventory_d8'}),
    [D12]: Object.freeze({select: 'unit_ventory_d12'}),
  }),
  [FACTIONS.DWARVES]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_dwarves_d6'}),
    [D10]: Object.freeze({select: 'unit_dwarves_d10'}),
    [D20]: Object.freeze({select: 'unit_dwarves_d20'}),
  }),
  [FACTIONS.AQUARS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_aquars_d6', move: 'unit_aquars_move_d6'}),
    [D10]: Object.freeze({select: 'unit_aquars_d10', move: 'unit_aquars_move_d10'}),
    [D20]: Object.freeze({select: 'unit_aquars_d20', move: 'unit_aquars_move'}),
    [D8]: Object.freeze({select: 'unit_aquars_d8', move: 'unit_aquars_mount_move_d8'}),
    [D12]: Object.freeze({select: 'unit_aquars_d12', move: 'unit_aquars_mount_move'}),
  }),
  [FACTIONS.DARK_ELVES]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_dark_elves_d6'}),
    [D10]: Object.freeze({select: 'unit_dark_elves_d10'}),
    [D20]: Object.freeze({select: 'unit_dark_elves_d20'}),
    [D8]: Object.freeze({select: 'unit_dark_elves_d8', move: 'unit_dark_elves_mount_move'}),
    [D12]: Object.freeze({select: 'unit_dark_elves_d12', move: 'unit_dark_elves_mount_move'}),
  }),
  [FACTIONS.DOPPLERS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_dopplers_d6'}),
    [D10]: Object.freeze({select: 'unit_dopplers_d10'}),
    [D20]: Object.freeze({select: 'unit_dopplers_d20'}),
    [D8]: Object.freeze({select: 'unit_dopplers_d8'}),
    [D12]: Object.freeze({select: 'unit_dopplers_d12'}),
  }),
  [FACTIONS.DRAGONS]: Object.freeze({
    [D20]: Object.freeze({select: 'unit_dragons_d20', move: 'unit_dragons_move'}),
  }),
  [FACTIONS.ENTS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_ents_d6', move: 'unit_ents_move_d6'}),
    [D10]: Object.freeze({select: 'unit_ents_d10', move: 'unit_ents_move_d10'}),
    [D20]: Object.freeze({select: 'unit_ents_d20', move: 'unit_ents_move'}),
    [D8]: Object.freeze({select: 'unit_ents_d8', move: 'unit_ents_mount_move'}),
    [D12]: Object.freeze({select: 'unit_ents_d12', move: 'unit_ents_lion_move'}),
  }),
  [FACTIONS.FENIX]: Object.freeze({
    [D20]: Object.freeze({select: 'unit_fenix_d20', move: 'unit_fenix_move'}),
  }),
  [FACTIONS.GREMLINS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_gremlins_d6'}),
    [D20]: Object.freeze({
      select: 'unit_gremlins_d20',
      move: 'unit_gremlins_d20_move',
    }),
  }),
  [FACTIONS.MUSHROOM_PEOPLE]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_mushroom_people_d6', move: 'unit_mushroom_people_move_d6'}),
    [D10]: Object.freeze({select: 'unit_mushroom_people_d10', move: 'unit_mushroom_people_move_d10'}),
    [D20]: Object.freeze({select: 'unit_mushroom_people_d20', move: 'unit_mushroom_people_move'}),
    [D8]: Object.freeze({select: 'unit_mushroom_people_d8', move: 'unit_dark_elves_mount_move'}),
    [D12]: BOAR,
  }),
  [FACTIONS.SPIRITS]: Object.freeze({
    [D10]: Object.freeze({select: 'unit_spirits_d10', move: 'unit_spirits_move'}),
  }),
  [FACTIONS.TECHNODWARVES]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_technodwarves_d6', move: 'unit_technodwarves_move_d6'}),
    [D10]: Object.freeze({select: 'unit_technodwarves_d10', move: 'unit_technodwarves_move_d10'}),
    [D20]: Object.freeze({select: 'unit_technodwarves_d20', move: 'unit_technodwarves_move'}),
  }),
  [FACTIONS.UNDEAD]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_undead_d6', move: 'unit_undead_move_d6'}),
    [D10]: Object.freeze({select: 'unit_undead_d10', move: 'unit_undead_move_d10'}),
    [D20]: Object.freeze({select: 'unit_undead_d20', move: 'unit_undead_move'}),
    [D8]: Object.freeze({select: 'unit_undead_d8', move: 'unit_undead_ghost_move'}),
    [D12]: Object.freeze({select: 'unit_undead_d12', move: 'unit_undead_vampire_move'}),
  }),
  [FACTIONS.WIZARDS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_wizards_d6'}),
    [D10]: Object.freeze({select: 'unit_wizards_d10'}),
    [D20]: Object.freeze({select: 'unit_wizards_d20'}),
    [D8]: Object.freeze({select: 'unit_wizards_d8'}),
    [D12]: Object.freeze({select: 'unit_wizards_d12'}),
  }),
  [FACTIONS.INSECTOIDS]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_insectoids_d6', move: 'unit_insectoids_move_d6'}),
    [D10]: Object.freeze({select: 'unit_insectoids_d10', move: 'unit_insectoids_move_d10'}),
    [D20]: Object.freeze({select: 'unit_insectoids_d20', move: 'unit_insectoids_move'}),
    [D8]: Object.freeze({select: 'unit_insectoids_d8', move: 'unit_insectoids_mount_move_d8'}),
    [D12]: Object.freeze({select: 'unit_insectoids_d12', move: 'unit_insectoids_mount_move'}),
  }),
  [FACTIONS.RATMEN]: Object.freeze({
    [D6]: Object.freeze({select: 'unit_ratmen_d6', move: 'unit_ratmen_move_d6'}),
    [D10]: Object.freeze({select: 'unit_ratmen_d10', move: 'unit_ratmen_move_d10'}),
    [D20]: Object.freeze({select: 'unit_ratmen_d20', move: 'unit_ratmen_move'}),
    [D8]: Object.freeze({select: 'unit_ratmen_d8', move: 'unit_ratmen_mount_move_d8'}),
    [D12]: Object.freeze({select: 'unit_ratmen_d12', move: 'unit_ratmen_mount_move'}),
  }),
  [FACTIONS.GIANTS]: Object.freeze({
    [D20]: Object.freeze({select: 'unit_giants_d20', move: 'unit_giants_move'}),
  }),
});

/**
 * Отряды, которые звучат своими звуками и без открытого образа: великан и без
 * него нарисован великаном (иконка unit_d20_giants_default), а не общим
 * силуэтом воина; у дракона и автоматона гремлинов обычный облик такой же особый.
 * Пехота гремлинов без образа звучит общим звуком
 */
const UNIT_TYPES_WITH_SOUNDS_WITHOUT_SKIN = Object.freeze({
  [FACTIONS.GIANTS]: Object.freeze([D20]),
  [FACTIONS.DRAGONS]: Object.freeze([D20]),
  [FACTIONS.GREMLINS]: Object.freeze([D20]),
});

/** Фракции, которым файлы звуков нужны и без открытого образа */
export const FACTIONS_WITH_UNIT_SOUNDS_WITHOUT_SKIN = Object.freeze(Object.keys(UNIT_TYPES_WITH_SOUNDS_WITHOUT_SKIN));

/**
 * @param {string} factionId
 * @param {string} unitType
 * @returns {boolean} звучит ли отряд своими звуками и без открытого образа
 */
export const checkIfUnitSoundsWithoutSkin = (factionId, unitType) => Boolean(
  UNIT_TYPES_WITH_SOUNDS_WITHOUT_SKIN[factionId]?.includes(unitType)
);

const toSoundIds = (value) => (Array.isArray(value) ? value : [value]).filter(Boolean);

/**
 * Фракции, чьим отрядам нужен звук: по ним решается, качать ли его файл в
 * партии (factionIds у отложенного звука)
 * @param {string} soundId
 * @returns {string[]}
 */
export const getFactionUnitSoundFactionIds = (soundId) => Object.keys(FACTION_UNIT_SOUNDS).filter((factionId) => (
  Object.values(FACTION_UNIT_SOUNDS[factionId]).some(({select, move}) => (
    toSoundIds(select).includes(soundId) || toSoundIds(move).includes(soundId)
  ))
));
